import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardCheck, Loader2, Plus, Trash2, Image as ImgIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CONDITION_OPTIONS = [
    { value: "good", label: "Good" },
    { value: "minor_wear", label: "Minor wear" },
    { value: "damaged", label: "Damaged" },
    { value: "lost", label: "Lost" },
];
const MAX_PHOTOS = 10;
const ALLOWED_PHOTO_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic"];

const fmtMoney = (cents) => (Number(cents || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

/**
 * Phase DP-R2 — Equipment inspection card.
 *
 * Props:
 *  - rental: the rental doc (must include .id, .status, .inspection?)
 *  - onChange: called after a successful mutation (parent reloads rental)
 *  - readOnly: if true, hide all edit controls (used when rental.status === "ended")
 */
export default function InspectionCard({ rental, onChange, readOnly = false }) {
    const insp = rental?.inspection || null;
    const [editing, setEditing] = useState(false);
    const [condition, setCondition] = useState(insp?.condition || "");
    const [notes, setNotes] = useState(insp?.notes || "");
    const [damages, setDamages] = useState(() => (insp?.damages || []).map(d => ({
        id: d.id, description: d.description, amount_str: ((d.amount_cents || 0) / 100).toFixed(2),
    })));
    const [busy, setBusy] = useState(false);
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        // Re-sync from server only when NOT actively editing — otherwise a
        // background reload (e.g. after a photo upload) would blow away the
        // admin's in-progress condition / notes / damages selections.
        if (editing) return;
        setCondition(insp?.condition || "");
        setNotes(insp?.notes || "");
        setDamages((insp?.damages || []).map(d => ({
            id: d.id, description: d.description,
            amount_str: ((d.amount_cents || 0) / 100).toFixed(2),
        })));
    }, [editing, rental?.id, insp?.inspected_at]);

    const photos = insp?.photos || [];
    const submitted = !!insp?.inspected_at;
    const damagesTotalCents = damages.reduce((sum, d) => {
        const v = Math.round(parseFloat(d.amount_str || "0") * 100);
        return sum + (Number.isFinite(v) && v > 0 ? v : 0);
    }, 0);

    const onAddDamage = () => setDamages(d => [...d, { id: `tmp_${Date.now()}_${Math.random()}`, description: "", amount_str: "" }]);
    const onUpdateDamage = (idx, patch) => setDamages(d => d.map((x, i) => i === idx ? { ...x, ...patch } : x));
    const onRemoveDamage = (idx) => setDamages(d => d.filter((_, i) => i !== idx));

    const canSave = !!condition;

    const onSave = async () => {
        // Validate damage rows client-side before posting.
        const clean = [];
        for (let i = 0; i < damages.length; i++) {
            const d = damages[i];
            const desc = (d.description || "").trim();
            const amt = Math.round(parseFloat(d.amount_str || "0") * 100);
            if (!desc) { toast.error(`Damage #${i + 1} needs a description`); return; }
            if (!Number.isFinite(amt) || amt <= 0) { toast.error(`Damage #${i + 1} needs a positive amount`); return; }
            if (desc.length > 500) { toast.error(`Damage #${i + 1} description is too long`); return; }
            clean.push({ description: desc, amount_cents: amt });
        }
        setBusy(true);
        try {
            await api.post(`/admin/rentals/${rental.id}/inspection`, {
                condition, notes, damages: clean,
            });
            toast.success(submitted ? "Inspection updated" : "Inspection submitted");
            setEditing(false);
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const onPickPhoto = () => fileRef.current?.click();
    const onPhotosSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        // Client-side mime + count guard so we don't bounce off the server.
        const bad = files.find(f => !ALLOWED_PHOTO_MIME.includes(f.type));
        if (bad) { toast.error(`${bad.name}: unsupported file type`); return; }
        if (photos.length + files.length > MAX_PHOTOS) {
            toast.error(`Maximum ${MAX_PHOTOS} photos per inspection`);
            return;
        }
        const fd = new FormData();
        files.forEach(f => fd.append("photos", f));
        setBusy(true);
        try {
            await api.post(`/admin/rentals/${rental.id}/inspection/photos`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} added`);
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const onDeletePhoto = async (photo) => {
        if (!window.confirm(`Delete photo "${photo.filename}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/rentals/${rental.id}/inspection/photos/${photo.id}`);
            toast.success("Photo deleted");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const statusPill = useMemo(() => {
        if (!insp) return { label: "Not started", className: "bg-slate-100 text-slate-700" };
        if (!submitted) return { label: "In progress", className: "bg-amber-100 text-amber-800" };
        return {
            label: `Submitted ${fmtDate(insp.inspected_at)}${insp.inspector_name ? ` by ${insp.inspector_name}` : ""}`,
            className: "bg-emerald-100 text-emerald-800",
        };
    }, [insp, submitted]);

    return (
        <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="inspection-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <ClipboardCheck size={18} className="text-blue-900" />
                    <h2 className="text-base font-semibold text-slate-900">Equipment Inspection</h2>
                    <span className={`text-xs px-2 py-0.5 rounded ${statusPill.className}`}
                        data-testid="inspection-status">{statusPill.label}</span>
                </div>
                {!readOnly && !editing && (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}
                        data-testid="inspection-edit">
                        {submitted ? "Revise inspection" : "Start inspection"}
                    </Button>
                )}
            </div>

            {/* Read-mode summary (when not editing OR readOnly) */}
            {(!editing || readOnly) && submitted && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <dt className="text-xs text-slate-500">Condition</dt>
                        <dd className="text-slate-900 font-medium">
                            {CONDITION_OPTIONS.find(o => o.value === insp.condition)?.label || insp.condition || "—"}
                        </dd>
                        {insp.notes && (
                            <>
                                <dt className="text-xs text-slate-500 mt-3">Notes</dt>
                                <dd className="text-slate-700 whitespace-pre-wrap">{insp.notes}</dd>
                            </>
                        )}
                    </div>
                    <div>
                        <dt className="text-xs text-slate-500">Damages</dt>
                        {(insp.damages || []).length === 0
                            ? <dd className="text-slate-500 italic">No damages recorded.</dd>
                            : (
                                <ul className="mt-1 space-y-1">
                                    {insp.damages.map(d => (
                                        <li key={d.id} className="flex justify-between gap-3 text-sm">
                                            <span className="text-slate-800">{d.description}</span>
                                            <span className="text-slate-900 font-medium">{fmtMoney(d.amount_cents)}</span>
                                        </li>
                                    ))}
                                    <li className="flex justify-between border-t border-slate-200 pt-1 mt-1 text-sm font-semibold">
                                        <span>Total</span><span>{fmtMoney((insp.damages || []).reduce((s, d) => s + (d.amount_cents || 0), 0))}</span>
                                    </li>
                                </ul>
                            )}
                    </div>
                </div>
            )}

            {!editing && (
                <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2">{photos.length} of {MAX_PHOTOS} photo{photos.length === 1 ? "" : "s"}</p>
                    <PhotoStrip photos={photos} rentalId={rental.id}
                        readOnly={readOnly} onPreview={setPreviewPhoto} onDelete={onDeletePhoto} />
                </div>
            )}

            {editing && !readOnly && (
                <div className="mt-5 space-y-5">
                    {/* Condition */}
                    <div className="space-y-2" data-testid="inspection-condition">
                        <Label>Condition <span className="text-red-500">*</span></Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {CONDITION_OPTIONS.map(opt => (
                                <label key={opt.value}
                                    data-testid={`inspection-condition-${opt.value}`}
                                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                                        condition === opt.value
                                            ? "border-blue-300 bg-blue-50/60"
                                            : "border-slate-200 hover:bg-slate-50"
                                    }`}>
                                    <input type="radio" name="dpr2-condition" value={opt.value}
                                        checked={condition === opt.value}
                                        onChange={() => setCondition(opt.value)} />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-1.5">
                        <Label>Notes (optional)</Label>
                        <Textarea rows={3} value={notes}
                            onChange={(e) => setNotes(e.target.value.slice(0, 2200))}
                            placeholder="Anything noteworthy about the equipment return."
                            data-testid="inspection-notes" />
                        <p className="text-[11px] text-slate-500">{notes.length}/2000</p>
                    </div>

                    {/* Photos */}
                    <div className="space-y-2" data-testid="inspection-photo-uploader">
                        <div className="flex items-center justify-between">
                            <Label>Photos ({photos.length} / {MAX_PHOTOS})</Label>
                            <Button type="button" variant="outline" size="sm" onClick={onPickPhoto}
                                disabled={photos.length >= MAX_PHOTOS || busy}
                                data-testid="inspection-photos-add">
                                <ImgIcon size={14} className="mr-1.5" /> Add photo
                            </Button>
                            <input ref={fileRef} type="file" multiple accept={ALLOWED_PHOTO_MIME.join(",") + ",.heic"}
                                className="hidden" onChange={onPhotosSelected}
                                data-testid="inspection-photos-input" />
                        </div>
                        <PhotoStrip photos={photos} rentalId={rental.id}
                            readOnly={false} onPreview={setPreviewPhoto} onDelete={onDeletePhoto} />
                    </div>

                    {/* Damages */}
                    <div className="space-y-2" data-testid="inspection-damages">
                        <div className="flex items-center justify-between">
                            <Label>Damage charges</Label>
                            <Button type="button" variant="outline" size="sm" onClick={onAddDamage}
                                data-testid="inspection-damages-add">
                                <Plus size={14} className="mr-1.5" /> Add damage
                            </Button>
                        </div>
                        {damages.length === 0 ? (
                            <p className="text-sm text-slate-500 italic">No damages. The damages invoice will not be created.</p>
                        ) : (
                            <div className="space-y-2">
                                {damages.map((d, idx) => (
                                    <div key={d.id} className="flex items-start gap-2"
                                        data-testid={`inspection-damages-row-${idx}`}>
                                        <Input value={d.description} placeholder="e.g. Replace side mirror"
                                            onChange={e => onUpdateDamage(idx, { description: e.target.value })}
                                            className="flex-1"
                                            data-testid={`inspection-damages-row-${idx}-desc`} />
                                        <div className="relative w-32">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                                            <Input type="number" min="0.01" step="0.01" value={d.amount_str}
                                                onChange={e => onUpdateDamage(idx, { amount_str: e.target.value })}
                                                className="pl-5 text-right"
                                                data-testid={`inspection-damages-row-${idx}-amount`} />
                                        </div>
                                        <Button type="button" variant="ghost" size="icon"
                                            onClick={() => onRemoveDamage(idx)}
                                            data-testid={`inspection-damages-row-${idx}-remove`}>
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                ))}
                                <div className="flex justify-end text-sm font-medium text-slate-900 pr-12"
                                    data-testid="inspection-damages-total">
                                    Total: {fmtMoney(damagesTotalCents)}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={onSave} disabled={!canSave || busy}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="inspection-save">
                            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}
                            {submitted ? "Save changes" : "Submit inspection"}
                        </Button>
                    </div>
                </div>
            )}

            <FilePreviewModal
                open={!!previewPhoto}
                onOpenChange={(v) => !v && setPreviewPhoto(null)}
                file={previewPhoto ? {
                    id: previewPhoto.id,
                    filename: previewPhoto.filename,
                    mime_type: previewPhoto.mime_type,
                    status: "viewable",
                } : null}
                downloadUrl={previewPhoto
                    ? `/admin/rentals/${rental.id}/inspection/photos/${previewPhoto.id}`
                    : null}
                mode="customer"
            />
        </div>
    );
}

function PhotoStrip({ photos, rentalId, readOnly, onPreview, onDelete }) {
    if (!photos.length) {
        return <p className="text-xs text-slate-500 italic">No photos uploaded.</p>;
    }
    return (
        <div className="flex flex-wrap gap-2" data-testid="inspection-photo-strip">
            {photos.map(p => (
                <PhotoTile key={p.id} photo={p} rentalId={rentalId}
                    readOnly={readOnly} onPreview={onPreview} onDelete={onDelete} />
            ))}
        </div>
    );
}

function PhotoTile({ photo, rentalId, readOnly, onPreview, onDelete }) {
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        let revoked = false;
        let url = null;
        api.get(`/admin/rentals/${rentalId}/inspection/photos/${photo.id}`, { responseType: "blob" })
            .then(({ data }) => {
                if (revoked) return;
                url = URL.createObjectURL(data);
                setBlobUrl(url);
            })
            .catch(() => setBlobUrl(null));
        return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
    }, [photo.id, rentalId]);
    return (
        <div className="relative w-24 h-24 rounded border border-slate-200 overflow-hidden group bg-slate-50"
            data-testid={`inspection-photo-${photo.id}`}>
            {blobUrl ? (
                <button type="button" onClick={() => onPreview(photo)} className="block w-full h-full">
                    <img src={blobUrl} alt={photo.filename}
                        className="w-full h-full object-cover" />
                </button>
            ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                    <ImgIcon size={20} />
                </div>
            )}
            {!readOnly && (
                <button type="button" onClick={() => onDelete(photo)}
                    className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`inspection-photo-${photo.id}-delete`}
                    title="Delete photo">
                    <Trash2 size={12} className="text-red-600" />
                </button>
            )}
        </div>
    );
}

export { CONDITION_OPTIONS, MAX_PHOTOS };
export const __dpr2 = { fmtMoney, fmtDate };  // for tests
